import type { Species } from '../../types';
import { el, ensureStyle } from '../dom';
import { getSpecies, speciesList } from '../../core/region';
import { compareCandidates } from '../../core/compare';
import { speciesPhoto } from './species-photo';
import { t } from '../../strings';

// "Compare to…" — a dropdown over 198 birds, which is only usable because it
// is not really a dropdown over 198 birds.
//
// Two things make it work. It OPENS on the birds that are plausibly wanted:
// the manifest's own confusables for the bird you are standing on, then its
// family, then its order, then the region (see compareCandidates). And it
// FILTERS on the same haystack the Library does — common name, binomial,
// family — so the moment you know which bird you want, three letters get you
// there regardless of which group it landed in.
//
// It is a popover anchored under whatever opened it, clamped into the viewport
// rather than allowed to hang off the edge, because on a phone the trigger is
// a pane header about 170px wide and a menu that inherited that width would be
// a column of wrapped names. Scrim behind it, Escape and click-outside to
// close: the ordinary contract for a menu that covers content.

ensureStyle(
  'species-picker-css',
  `
  .spick-scrim { position:fixed; inset:0; z-index:60; background:rgba(0,0,0,.18); }
  .spick {
    position:fixed; z-index:61; display:flex; flex-direction:column;
    background:var(--card); color:var(--ink);
    border:1.5px solid var(--hairline-strong); border-radius:var(--radius-lg);
    box-shadow:var(--shadow); overflow:hidden;
  }
  .spick .spick-head { padding:10px 10px 8px; border-bottom:1.5px solid var(--hairline); }
  .spick .spick-head .input { padding:10px 12px; font-size:15px; }
  .spick .spick-list { overflow-y:auto; overscroll-behavior:contain; padding:4px 0 8px; }
  /* Sticky like the Library's family headings, and for the same reason: the
     group you are scrolling through stays named while you scroll it. */
  .spick .spick-group { position:sticky; top:0; z-index:1; background:var(--card);
    padding:8px 12px 5px; }
  .spick .spick-row { display:flex; align-items:center; gap:10px; width:100%;
    padding:7px 12px; background:none; border:none; text-align:left; }
  .spick .spick-row:hover, .spick .spick-row.on { background:var(--sunk); }
  .spick .spick-row .spphoto { flex:none; width:34px; height:34px;
    border-radius:9px; border:1.5px solid var(--hairline); }
  .spick .spick-row .names { flex:1; min-width:0; }
  .spick .spick-row .common { display:block; font-family:var(--font-display);
    font-size:15px; line-height:1.2; }
  .spick .spick-row .sci { display:block; font-size:11.5px; color:var(--ink-soft);
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .spick .spick-none { padding:18px 14px; text-align:center; }
  .spick [hidden] { display:none; }
`,
);

/** How tall the popover is allowed to get, as a fraction of the viewport. */
const MAX_HEIGHT = 0.62;

/** Narrower than this and a name wraps; the popover shifts sideways instead. */
const MIN_WIDTH = 264;

/** Breathing room between the popover and the edge of the screen. */
const EDGE = 10;

export interface SpeciesPickerOptions {
  /**
   * The bird the offer is being made about — whose confusables, family and
   * order decide the order of the list.
   */
  anchor: Species;
  /** Codes already on screen, kept out of the list. */
  exclude?: string[];
  onPick: (code: string) => void;
}

/**
 * Open the picker under `near`. Resolves nothing — the pick arrives through
 * `onPick`, and dismissal is silent, which is what a menu closing means.
 */
export function openSpeciesPicker(near: HTMLElement, opts: SpeciesPickerOptions): void {
  const all = speciesList();
  const groups = compareCandidates(opts.anchor, all, opts.exclude);

  const input = el('input.input', {
    type: 'search',
    placeholder: t('species.picker.placeholder'),
    autocomplete: 'off',
    'aria-label': t('species.picker.label'),
  }) as HTMLInputElement;

  const list = el('div.spick-list', { role: 'listbox' });
  const none = el('div.spick-none.meta', {}, '');
  none.hidden = true;

  const panel = el(
    'div.spick',
    { role: 'dialog', 'aria-label': t('species.picker.dialog', { name: opts.anchor.common }) },
    el('div.spick-head', {}, input),
    none,
    list,
  );
  const scrim = el('div.spick-scrim', {});

  // Every row is built once and filtering only toggles visibility — the same
  // trade the Library makes, and the reason the thumbnails have to be lazy.
  const rows: { code: string; node: HTMLButtonElement; haystack: string }[] = [];
  const groupNodes: { node: HTMLElement; rows: HTMLElement[] }[] = [];

  for (const group of groups) {
    const head = el('div.spick-group.label', {}, group.label);
    const block = el('div.spick-block', {}, head);
    const nodes: HTMLElement[] = [];
    for (const code of group.codes) {
      const sp = getSpecies(code);
      if (!sp) continue;
      const row = el(
        'button.spick-row',
        { type: 'button', role: 'option' },
        speciesPhoto(sp.photos[0], { thumb: true, lazy: true, alt: '' }).element,
        el(
          'span.names',
          {},
          el('span.common', {}, sp.common),
          el('span.sci', {}, sp.sci),
        ),
      ) as HTMLButtonElement;
      row.addEventListener('click', () => {
        close();
        opts.onPick(code);
      });
      block.append(row);
      nodes.push(row);
      rows.push({
        code,
        node: row,
        haystack: `${sp.common} ${sp.sci} ${sp.family ?? ''}`.toLowerCase(),
      });
    }
    list.append(block);
    groupNodes.push({ node: block, rows: nodes });
  }

  /** The row the keyboard is on, as an index into the currently visible ones. */
  let cursor = -1;

  const visible = (): HTMLButtonElement[] =>
    rows.filter((r) => !r.node.hidden).map((r) => r.node);

  const paintCursor = (): void => {
    const shown = visible();
    cursor = Math.max(-1, Math.min(cursor, shown.length - 1));
    for (const r of rows) r.node.classList.remove('on');
    const at = shown[cursor];
    if (at) {
      at.classList.add('on');
      at.scrollIntoView({ block: 'nearest' });
    }
  };

  const apply = (): void => {
    // Every word has to match somewhere, so "sparrow song" and "song sparrow"
    // find the same bird — the Library's rule, because it is the same haystack.
    const terms = input.value.toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0;
    for (const r of rows) {
      const hit = terms.every((t) => r.haystack.includes(t));
      r.node.hidden = !hit;
      if (hit) shown++;
    }
    for (const g of groupNodes) g.node.hidden = g.rows.every((n) => n.hidden);
    none.hidden = shown > 0;
    none.textContent = shown ? '' : t('species.picker.none', { query: input.value.trim() });
    // Typing moves the offer, so the highlight follows the first thing left
    // rather than staying on a row that has just been filtered out from under it.
    cursor = terms.length && shown ? 0 : -1;
    paintCursor();
  };

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', place);
    window.removeEventListener('hashchange', close);
    scrim.remove();
    panel.remove();
    // Back to whatever opened the menu, so a keyboard user is not dropped at
    // the top of the document.
    near.focus?.();
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    // A menu that is up owns all four arrows, whether it uses them or not: the
    // screens this opens over read them too — the side-by-side view changes
    // what is being compared, the species page walks to the next bird — and
    // one of those moving underneath an open menu is how you end up picking a
    // bird to compare with a page you are no longer on.
    if (e.key.startsWith('Arrow')) {
      e.preventDefault();
      e.stopPropagation();
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const shown = visible();
      if (!shown.length) return;
      cursor = (cursor + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
      paintCursor();
      return;
    }
    if (e.key === 'Enter') {
      const shown = visible();
      // Only when the keyboard has actually walked onto a row: Enter with
      // nothing highlighted belongs to the search field, not to a guess.
      if (cursor < 0 || !shown[cursor]) return;
      e.preventDefault();
      // Stopped as well as prevented: the screen behind this menu has an Enter of
      // its own (a quiz round advances on it), and picking a bird out of a menu
      // must not also press the button underneath the menu.
      e.stopPropagation();
      shown[cursor].click();
    }
  }

  /**
   * Put the panel under its trigger, then drag it back inside the screen.
   *
   * Fixed positioning against the trigger's viewport rect, so this has to be
   * redone on a resize (a rotation, or the on-screen keyboard opening under it
   * on Android, which is a viewport resize).
   */
  function place(): void {
    const box = near.getBoundingClientRect();
    const width = Math.min(
      Math.max(box.width, MIN_WIDTH),
      window.innerWidth - EDGE * 2,
    );
    const maxHeight = Math.round(window.innerHeight * MAX_HEIGHT);
    panel.style.width = `${width}px`;
    panel.style.maxHeight = `${maxHeight}px`;

    let left = box.left;
    if (left + width > window.innerWidth - EDGE) left = window.innerWidth - EDGE - width;
    if (left < EDGE) left = EDGE;

    // Below by preference, above when there is more room up there — a header
    // near the foot of a short screen would otherwise get a menu two rows tall.
    const below = window.innerHeight - box.bottom - EDGE;
    const above = box.top - EDGE;
    const drop = below >= Math.min(maxHeight, 240) || below >= above;
    panel.style.left = `${Math.round(left)}px`;
    if (drop) {
      panel.style.top = `${Math.round(box.bottom + 6)}px`;
      panel.style.bottom = 'auto';
      panel.style.maxHeight = `${Math.min(maxHeight, below)}px`;
    } else {
      panel.style.bottom = `${Math.round(window.innerHeight - box.top + 6)}px`;
      panel.style.top = 'auto';
      panel.style.maxHeight = `${Math.min(maxHeight, above)}px`;
    }
  }

  input.addEventListener('input', apply);
  scrim.addEventListener('click', close);
  // Capture, so Escape closes the menu rather than reaching the surface behind
  // it — which has its own Escape handler that would close the whole view.
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('resize', place);
  // Any navigation at all leaves the menu orphaned over a screen it was never
  // opened from.
  window.addEventListener('hashchange', close);

  document.body.append(scrim, panel);
  apply();
  place();
  input.focus();
}
